import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AppIcon,
  BriefcaseBusinessIcon,
  CircleCheckIcon,
  ClockIcon,
  MailCheckIcon,
  SparklesIcon,
  XIcon,
} from '../../components/icons';
import { apiClient } from '../../shared/services/api';
import {
  sendWorkEmailOtp,
  verifyWorkEmailOtp,
  normalizeEmail,
  isValidEmailFormat,
  isPersonalOrDisposableEmail,
} from '../../shared/services/workEmailVerificationService';
import { SuccessStoryStep, type SuccessStoryPayload } from './SuccessStoryStep';
import { ShareCardStep } from './ShareCardStep';
import './HiredUpgradeModal.css';

const CORPORATE_JOB_IMAGE = '/images/illustration-depicting-corporate-job.png';
const OTP_LENGTH = 6;
/** Where a freshly-upgraded working professional should land. */
const REFERRER_DASHBOARD_PATH = '/referral-dashboard';

/**
 * Success story step is commented out / disabled for now. When false, a
 * successful upgrade skips straight to the share step and no success story is
 * created. Flip to `true` to bring the "Share your story" step back — the step's
 * component, handler and JSX are all left intact below.
 */
const SUCCESS_STORY_ENABLED = false;

type ModalStep = 'form' | 'otp' | 'story' | 'share';

interface HiredUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Fired once the caller has been upgraded to a working professional. */
  onUpgraded?: () => void;
}

function formatSeconds(total: number): string {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
}

export function HiredUpgradeModal({ isOpen, onClose, onUpgraded }: HiredUpgradeModalProps) {
  const [step, setStep] = useState<ModalStep>('form');

  // ── Form fields ───────────────────────────────────────────────────────────
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [referrerCode, setReferrerCode] = useState('');
  const [error, setError] = useState('');

  // ── OTP challenge state ─────────────────────────────────────────────────────
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpNotice, setOtpNotice] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [expiresIn, setExpiresIn] = useState(0);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [devCode, setDevCode] = useState('');
  const otpInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  // ── Post-upgrade / success-story state ──────────────────────────────────────
  const [upgradeRequestId, setUpgradeRequestId] = useState<number | null>(null);
  const [storySubmitting, setStorySubmitting] = useState(false);
  const [storyError, setStoryError] = useState('');
  const [shareCompanyName, setShareCompanyName] = useState('');
  const [shareRoleTitle, setShareRoleTitle] = useState('');
  /** Whether the account was actually upgraded this session (drives the redirect). */
  const hasUpgradedRef = useRef(false);

  // ── Countdown timers (resend cooldown + code expiry) ────────────────────────
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => window.clearInterval(id);
  }, [cooldown]);

  useEffect(() => {
    if (expiresIn <= 0) return;
    const id = window.setInterval(() => setExpiresIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => window.clearInterval(id);
  }, [expiresIn]);

  if (!isOpen) return null;

  const resetAndClose = () => {
    const upgraded = hasUpgradedRef.current;

    setStep('form');
    setCompanyName('');
    setCompanyEmail('');
    setReferrerCode('');
    setError('');
    setOtpDigits(Array(OTP_LENGTH).fill(''));
    setSendingOtp(false);
    setVerifying(false);
    setUpgrading(false);
    setOtpError('');
    setOtpNotice('');
    setMaskedEmail('');
    setCooldown(0);
    setExpiresIn(0);
    setAttemptsRemaining(null);
    setDevCode('');
    setUpgradeRequestId(null);
    setStorySubmitting(false);
    setStoryError('');
    setShareCompanyName('');
    setShareRoleTitle('');
    hasUpgradedRef.current = false;

    onClose();

    // A hard navigation guarantees the auth profile is refetched so the app
    // renders the (now unlocked) working professional dashboard.
    if (upgraded) {
      window.location.assign(REFERRER_DASHBOARD_PATH);
    }
  };

  // ── Perform the privileged upgrade (guarded server-side by the OTP proof) ───
  const runUpgrade = async (): Promise<boolean> => {
    setUpgrading(true);
    const res = await apiClient.selfUpgradeToReferrer({
      company_name: companyName.trim(),
      referrer_code: referrerCode.trim().toUpperCase() || null,
    });
    setUpgrading(false);

    if (!res.success) return false;

    hasUpgradedRef.current = true;
    onUpgraded?.();
    setUpgradeRequestId(res.data?.upgrade_request_id ?? null);
    // Success story step disabled for now — skip straight to the share step.
    setStep(SUCCESS_STORY_ENABLED ? 'story' : 'share');
    return true;
  };

  // ── Step 1: validate + send the OTP to the company mail ID ──────────────────
  const handleSendOtp = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError('');

    const name = companyName.trim();
    const email = normalizeEmail(companyEmail);

    if (!name && !email) {
      setError('Please enter your company name and your company mail id.');
      return;
    }
    if (!name) {
      setError('Please enter your company name.');
      return;
    }
    if (!email) {
      setError('Please enter your company mail id.');
      return;
    }
    if (!isValidEmailFormat(email)) {
      setError('Please enter a valid company mail id.');
      return;
    }
    if (isPersonalOrDisposableEmail(email)) {
      setError("Use your company email address — personal mailboxes can't be verified.");
      return;
    }

    setSendingOtp(true);
    const res = await sendWorkEmailOtp(email);
    setSendingOtp(false);

    if (!res.success) {
      setError(res.error || 'Could not send the code. Please try again.');
      if (res.code === 'cooldown' && res.retryAfter) setCooldown(res.retryAfter);
      return;
    }

    setShareCompanyName(name);

    // Already proven this exact address before — skip straight to the upgrade.
    if (res.alreadyVerified) {
      const ok = await runUpgrade();
      if (!ok) {
        setError('We could not complete the upgrade. Please try again.');
      }
      return;
    }

    setOtpDigits(Array(OTP_LENGTH).fill(''));
    setMaskedEmail(res.maskedEmail || email);
    setAttemptsRemaining(res.maxAttempts ?? null);
    setCooldown(res.cooldownSeconds ?? 60);
    if (res.expiresAt) {
      setExpiresIn(Math.max(0, Math.round((new Date(res.expiresAt).getTime() - Date.now()) / 1000)));
    }
    setDevCode(res.devCode || '');
    setOtpError('');
    setOtpNotice(`We sent a 6-digit code to ${res.maskedEmail || email}. Check your inbox (and spam folder).`);
    setStep('otp');
    window.setTimeout(() => otpInputRefs.current[0]?.focus(), 60);
  };

  const handleResend = async () => {
    if (cooldown > 0 || sendingOtp || verifying || upgrading) return;
    const email = normalizeEmail(companyEmail);

    setSendingOtp(true);
    setOtpError('');
    const res = await sendWorkEmailOtp(email);
    setSendingOtp(false);

    if (!res.success) {
      setOtpError(res.error || 'Could not resend the code. Please try again.');
      if (res.code === 'cooldown' && res.retryAfter) setCooldown(res.retryAfter);
      return;
    }

    setOtpDigits(Array(OTP_LENGTH).fill(''));
    setAttemptsRemaining(res.maxAttempts ?? null);
    setCooldown(res.cooldownSeconds ?? 60);
    if (res.expiresAt) {
      setExpiresIn(Math.max(0, Math.round((new Date(res.expiresAt).getTime() - Date.now()) / 1000)));
    }
    if (res.devCode) setDevCode(res.devCode);
    setOtpNotice("A new code is on its way. Check your spam folder if you don't see it.");
    window.setTimeout(() => otpInputRefs.current[0]?.focus(), 60);
  };

  // ── Step 2: verify the entered code ─────────────────────────────────────────
  const submitOtp = async (code: string) => {
    if (code.length !== OTP_LENGTH || verifying || upgrading) return;

    setVerifying(true);
    setOtpError('');
    setOtpNotice('');
    const res = await verifyWorkEmailOtp(normalizeEmail(companyEmail), code);
    setVerifying(false);

    if (!res.success) {
      setOtpError(res.error || 'That code is not right. Please try again.');
      if (typeof res.attemptsRemaining === 'number') setAttemptsRemaining(res.attemptsRemaining);
      if (res.code === 'attempts_exhausted' || res.code === 'no_active_challenge') setExpiresIn(0);
      setOtpDigits(Array(OTP_LENGTH).fill(''));
      window.setTimeout(() => otpInputRefs.current[0]?.focus(), 60);
      return;
    }

    const ok = await runUpgrade();
    if (!ok) {
      setOtpError('We verified your email but the upgrade did not complete. Please try again.');
    }
  };

  const handleStorySubmit = async (payload: SuccessStoryPayload) => {
    if (!upgradeRequestId) {
      setStep('share');
      return;
    }
    setStorySubmitting(true);
    setStoryError('');
    try {
      await apiClient.createSuccessStory({
        upgrade_request_id: upgradeRequestId,
        referrer_id: payload.referrer_id,
        company_name: shareCompanyName,
        role_title: payload.role_title,
        quote: payload.quote,
        auto_generated_quote: payload.auto_generated_quote,
      });
      setShareRoleTitle(payload.role_title);
    } catch (err: any) {
      // Non-blocking: log but advance to share step regardless
      console.error('Failed to save success story:', err);
      setStoryError('Could not save your story, but you can still share!');
      setShareRoleTitle(payload.role_title);
    } finally {
      setStorySubmitting(false);
      setStep('share');
    }
  };

  // ── OTP digit-box input handlers ────────────────────────────────────────────
  const handleOtpChange = (index: number, raw: string) => {
    const typed = raw.replace(/\D/g, '');
    if (!typed) {
      const next = [...otpDigits];
      next[index] = '';
      setOtpDigits(next);
      return;
    }

    const next = [...otpDigits];
    for (let i = 0; i < typed.length && index + i < OTP_LENGTH; i++) {
      next[index + i] = typed[i];
    }
    setOtpDigits(next);

    const landing = Math.min(index + typed.length, OTP_LENGTH - 1);
    otpInputRefs.current[landing]?.focus();

    const assembled = next.join('');
    if (assembled.length === OTP_LENGTH && !assembled.includes('')) {
      void submitOtp(assembled);
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...otpDigits];
      if (next[index]) {
        next[index] = '';
        setOtpDigits(next);
      } else if (index > 0) {
        next[index - 1] = '';
        setOtpDigits(next);
        otpInputRefs.current[index - 1]?.focus();
      }
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      otpInputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      e.preventDefault();
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((d, i) => {
      next[i] = d;
    });
    setOtpDigits(next);
    otpInputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
    if (pasted.length === OTP_LENGTH) void submitOtp(pasted);
  };

  const otpValue = otpDigits.join('');
  const busy = verifying || upgrading;
  const showImageVisual = step === 'form' || step === 'otp' || step === 'story';

  return createPortal(
    <div className="hired-modal-backdrop" role="presentation" onClick={resetAndClose}>
      <div
        className="hired-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hired-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="hired-modal__aurora" aria-hidden />

        <button type="button" className="hired-modal__close" onClick={resetAndClose} aria-label="Close">
          <AppIcon icon={XIcon} size={16} />
        </button>

        <div className="hired-modal__layout">
          <div className="hired-modal__visual">
            <span className="hired-modal__visual-glow" aria-hidden />

            {showImageVisual ? (
              <>
                <span className="hired-modal__visual-badge">
                  <AppIcon icon={SparklesIcon} size={12} />
                  Career milestone
                </span>
                <div className="hired-modal__image-frame">
                  <img
                    src={CORPORATE_JOB_IMAGE}
                    alt="Corporate job celebration"
                    className="hired-modal__image"
                    width={400}
                    height={400}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </>
            ) : (
              <div className="hired-modal__success-visual">
                <span className="hired-modal__success-icon" aria-hidden>
                  <AppIcon icon={CircleCheckIcon} size={34} />
                </span>
                <p className="hired-modal__success-caption">
                  You&apos;re now a Working Professional on ReffConnect!
                </p>
              </div>
            )}
          </div>

          <div className="hired-modal__content">
            {step === 'form' ? (
              <form className="hired-modal__form" onSubmit={handleSendOtp} noValidate>
                <div className="hired-modal__scroll">
                  <div className="hired-modal__head">
                    <span className="hired-modal__head-icon" aria-hidden>
                      <AppIcon icon={BriefcaseBusinessIcon} size={18} />
                    </span>
                    <div>
                      <span className="hired-modal__eyebrow">Working Professional upgrade</span>
                      <h2 id="hired-modal-title" className="hired-modal__title">
                        Congratulations!
                      </h2>
                    </div>
                  </div>

                  <p className="hired-modal__text">
                    Congratulations on your new role! Now unlock your{' '}
                    <strong>Working Professional Dashboard</strong> on <strong>ReffConnect</strong> to offer
                    referrals, resume reviews, culture calls, and more to job seekers. We are so excited to
                    see you as a working professional.
                  </p>
                  <p className="hired-modal__subtext">
                    Enter the company that hired you, then verify your company mail ID with a one-time code to
                    instantly unlock your dashboard.
                  </p>

                  <div className="hired-modal__fields">
                    <div className="hired-modal__field">
                      <label className="hired-modal__label" htmlFor="hired-company-name">
                        Company name <span className="hired-modal__required">*</span>
                      </label>
                      <input
                        id="hired-company-name"
                        type="text"
                        className="hired-modal__input"
                        placeholder="e.g. Google, TCS, Infosys"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        disabled={sendingOtp}
                        autoComplete="organization"
                      />
                    </div>

                    <div className="hired-modal__field">
                      <label className="hired-modal__label" htmlFor="hired-company-email">
                        Company email ID <span className="hired-modal__required">*</span>
                      </label>
                      <input
                        id="hired-company-email"
                        type="email"
                        className="hired-modal__input"
                        placeholder="you@company.com"
                        value={companyEmail}
                        onChange={(e) => setCompanyEmail(e.target.value)}
                        disabled={sendingOtp}
                        autoComplete="work email"
                      />
                    </div>

                    <div className="hired-modal__field">
                      <label className="hired-modal__label" htmlFor="hired-referrer-code">
                        Referrer&apos;s unique user ID{' '}
                        <span className="hired-modal__optional">(optional)</span>
                      </label>
                      <input
                        id="hired-referrer-code"
                        type="text"
                        className="hired-modal__input"
                        placeholder="e.g. REFFR10042"
                        value={referrerCode}
                        onChange={(e) => setReferrerCode(e.target.value)}
                        disabled={sendingOtp}
                        autoComplete="off"
                        autoCapitalize="characters"
                        spellCheck={false}
                      />
                      <span className="hired-modal__hint">
                        Enter the unique ID of the ReffConnect member who referred you, if any.
                      </span>
                    </div>
                  </div>

                  {error && (
                    <p className="hired-modal__error" role="alert">
                      {error}
                    </p>
                  )}
                </div>

                <div className="hired-modal__actions">
                  <button
                    type="button"
                    className="hired-modal__btn hired-modal__btn--ghost"
                    onClick={resetAndClose}
                    disabled={sendingOtp}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="hired-modal__btn hired-modal__btn--primary"
                    disabled={sendingOtp}
                  >
                    {sendingOtp ? 'Sending code...' : 'Send OTP'}
                    {!sendingOtp && <span className="hired-modal__btn-shine" aria-hidden />}
                  </button>
                </div>
              </form>
            ) : step === 'otp' ? (
              <div className="hired-modal__form">
                <div className="hired-modal__scroll">
                  <div className="hired-modal__head">
                    <span className="hired-modal__head-icon" aria-hidden>
                      <AppIcon icon={MailCheckIcon} size={18} />
                    </span>
                    <div>
                      <span className="hired-modal__eyebrow">Verify company mail ID</span>
                      <h2 className="hired-modal__title">Enter verification code</h2>
                    </div>
                  </div>

                  <p className="hired-modal__subtext">
                    We emailed a 6-digit code to{' '}
                    <strong>{maskedEmail || companyEmail}</strong>. Enter it below to confirm this is your
                    work address.
                  </p>

                  <div className="hired-modal__otp" onPaste={handleOtpPaste}>
                    {otpDigits.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => {
                          otpInputRefs.current[index] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={1}
                        className={`hired-modal__otp-input ${otpError ? 'hired-modal__otp-input--error' : ''}`}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onFocus={(e) => e.currentTarget.select()}
                        disabled={busy}
                        aria-label={`Verification code digit ${index + 1} of ${OTP_LENGTH}`}
                      />
                    ))}
                  </div>

                  <div className="hired-modal__otp-meta">
                    {expiresIn > 0 ? (
                      <span className="hired-modal__otp-timer">
                        <AppIcon icon={ClockIcon} size={12} /> Expires in {formatSeconds(expiresIn)}
                      </span>
                    ) : (
                      <span />
                    )}
                    <span className="hired-modal__otp-resend">
                      Didn&apos;t get the code?{' '}
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={sendingOtp || cooldown > 0 || busy}
                      >
                        {sendingOtp
                          ? 'Sending…'
                          : cooldown > 0
                            ? `Resend in ${formatSeconds(cooldown)}`
                            : 'Resend'}
                      </button>
                    </span>
                  </div>

                  {attemptsRemaining !== null && attemptsRemaining < 5 && attemptsRemaining > 0 && (
                    <p className="hired-modal__otp-attempts">
                      {attemptsRemaining} attempt{attemptsRemaining === 1 ? '' : 's'} left
                    </p>
                  )}
                  {devCode && <p className="hired-modal__hint">Dev echo — code is {devCode}</p>}

                  {otpError && (
                    <p className="hired-modal__error" role="alert">
                      {otpError}
                    </p>
                  )}
                  {!otpError && otpNotice && (
                    <p className="hired-modal__error hired-modal__error--info" role="status">
                      {otpNotice}
                    </p>
                  )}

                  <p className="hired-modal__otp-terms">
                    By continuing, you confirm this is your company email and agree to ReffConnect&apos;s Terms
                    of Service &amp; Privacy Policy.
                  </p>
                </div>

                <div className="hired-modal__actions">
                  <button
                    type="button"
                    className="hired-modal__btn hired-modal__btn--ghost"
                    onClick={() => {
                      setStep('form');
                      setOtpError('');
                      setOtpNotice('');
                    }}
                    disabled={busy}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="hired-modal__btn hired-modal__btn--primary"
                    onClick={() => void submitOtp(otpValue)}
                    disabled={busy || otpValue.length !== OTP_LENGTH}
                  >
                    {upgrading ? 'Upgrading…' : verifying ? 'Verifying…' : 'Verify & Upgrade'}
                    {!busy && <span className="hired-modal__btn-shine" aria-hidden />}
                  </button>
                </div>
              </div>
            ) : step === 'story' ? (
              <div className="hired-modal__panel">
                {storyError && (
                  <p className="hired-modal__error hired-modal__error--info" role="status">
                    {storyError}
                  </p>
                )}
                <SuccessStoryStep
                  upgradeRequestId={upgradeRequestId ?? 0}
                  companyName={shareCompanyName}
                  onSubmit={handleStorySubmit}
                  onSkip={() => setStep('share')}
                  submitting={storySubmitting}
                />
              </div>
            ) : (
              <ShareCardStep
                companyName={shareCompanyName}
                roleTitle={shareRoleTitle || 'your new role'}
                onDone={resetAndClose}
              />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
